import React, { FC, ReactNode, createContext, useContext, useMemo, useState } from 'react';
import { Box, Tooltip } from '@mui/material';

// Shared building blocks for the Expert IQ equation explainers (simple + complex
// forms in AlgorithmCard). Each `Var` is hoverable and reveals what the symbol
// maps to in the scorer (expertMatch.js).
//
// Symbols and explainer cards are linked by a shared "highlight key". Hovering a
// symbol — or a card — sets the active key, and everything carrying that key
// (the matching symbol(s) in BOTH equations and the matching card(s) in BOTH
// rows) lights up together. That cross-link is what ties the simple form to the
// full one. Components fall back to a no-op when used outside a provider.

export const pct = (n: number) => `${(n * 100).toFixed(1)}%`;

export const MATH_FONT = 'Georgia, "Times New Roman", serif';

// Persistent tint applied to a symbol when its key is active.
export const HIGHLIGHT_BG = 'rgba(25,118,210,0.20)';

interface HighlightState {
  active: string | null;
  setActive: (k: string | null) => void;
}

const HighlightContext = createContext<HighlightState>({ active: null, setActive: () => {} });

export const useHighlight = () => useContext(HighlightContext);

// Wrap the equation(s) + explainer cards in one provider so they share the
// active highlight key.
export const HighlightProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [active, setActive] = useState<string | null>(null);
  const value = useMemo(() => ({ active, setActive }), [active]);
  return (
    <HighlightContext.Provider value={value}>
      {children}
    </HighlightContext.Provider>
  );
};

// A hoverable, italic math symbol with a tooltip describing what it means. When
// `k` is set it also participates in the shared highlight (hovering it activates
// `k`; it lights up whenever `k` is the active key).
export const Var: FC<{ title: string; k?: string; children: ReactNode }> = ({ title, k, children }) => {
  const { active, setActive } = useHighlight();
  const on = k != null && active === k;
  return (
    <Tooltip title={title} arrow placement="top" enterTouchDelay={0} leaveTouchDelay={4000}>
      <Box
        component="span"
        onMouseEnter={k != null ? () => setActive(k) : undefined}
        onMouseLeave={k != null ? () => setActive(null) : undefined}
        sx={{
          fontFamily: MATH_FONT,
          fontStyle: 'italic',
          cursor: 'help',
          borderBottom: '1px dotted var(--muted)',
          px: '1px',
          borderRadius: '3px',
          backgroundColor: on ? HIGHLIGHT_BG : 'transparent',
          transition: 'background-color .12s ease',
          '&:hover': { backgroundColor: HIGHLIGHT_BG },
        }}
      >
        {children}
      </Box>
    </Tooltip>
  );
};

export const Sub: FC<{ children: ReactNode }> = ({ children }) => (
  <Box component="sub" sx={{ fontSize: '0.65em' }}>{children}</Box>
);

// Plain (non-italic) operators / parens / numbers.
export const Op: FC<{ children: ReactNode }> = ({ children }) => (
  <Box component="span" sx={{ fontFamily: MATH_FONT, mx: '2px' }}>{children}</Box>
);

export const Sum: FC<{ top: ReactNode; bottom: ReactNode; size?: number; k?: string; title?: string }> = ({
  top,
  bottom,
  size = 1.7,
  k,
  title,
}) => {
  const { active, setActive } = useHighlight();
  const on = k != null && active === k;
  const glyph = (
    <Box
      onMouseEnter={k != null ? () => setActive(k) : undefined}
      onMouseLeave={k != null ? () => setActive(null) : undefined}
      sx={{
        fontSize: `${size}em`,
        lineHeight: 0.9,
        px: '1px',
        borderRadius: '3px',
        cursor: k != null ? 'help' : 'inherit',
        backgroundColor: on ? HIGHLIGHT_BG : 'transparent',
        transition: 'background-color .12s ease',
      }}
    >
      Σ
    </Box>
  );
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        verticalAlign: 'middle',
        mx: 0.5,
        fontFamily: MATH_FONT,
      }}
    >
      <Box sx={{ fontSize: '0.6em' }}>{top}</Box>
      {title ? <Tooltip title={title} arrow placement="top">{glyph}</Tooltip> : glyph}
      <Box sx={{ fontSize: '0.6em' }}>{bottom}</Box>
    </Box>
  );
};

export const Frac: FC<{ num: ReactNode; den: ReactNode }> = ({ num, den }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      flexDirection: 'column',
      alignItems: 'center',
      verticalAlign: 'middle',
      mx: 0.5,
    }}
  >
    <Box sx={{ px: 0.5 }}>{num}</Box>
    <Box sx={{ width: '100%', borderTop: '1px solid currentColor', my: '2px' }} />
    <Box sx={{ px: 0.5 }}>{den}</Box>
  </Box>
);

export const Bracket: FC<{ children: ReactNode }> = ({ children }) => (
  <Box component="span" sx={{ fontFamily: MATH_FONT, fontSize: '2.6em', fontWeight: 300, mx: '2px' }}>
    {children}
  </Box>
);
